import React, { useState } from 'react';
import { Alert, Box, Button, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { DateField, EmptyState, FormDialog, LoadingState, NumberField, StatusChip } from '../ui';
import {
  createScopedRebate,
  listVendorRebatePrograms,
  listVendorRebateStores,
  VENDOR_REBATE_PROGRAMS_QUERY_KEY,
  VENDOR_REBATE_STORES_QUERY_KEY,
  type VendorRebateProgram,
  type VendorRebateProgramStatus,
  type VendorStoreOption,
} from '../../services/vendorScopedRebatesApi';

const NS = 'vendor.scopedRebates';
const ALL_STORES = '__all__';

const statusTone = (status: VendorRebateProgramStatus) =>
  status === 'active' ? 'positive' : status === 'pending_store_approval' || status === 'draft' ? 'caution' : status === 'declined' ? 'problem' : 'neutral';

/**
 * Priya creates a rebate for a named store (or all her verified stores) with a
 * scope, and sees the status of every program she owns. Creating makes the
 * program active for the store at once; there is no Motzu review step. The
 * flat rebate form on the Rebate Programs tab is unchanged.
 */
export const VendorScopedRebatesTab: React.FC = () => {
  const { t } = useAppTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: '',
    termsKind: 'instant_rebate' as 'instant_rebate' | 'trade_credit',
    amount: '',
    startAt: '',
    endAt: '',
    store: ALL_STORES,
    scopeKind: 'all' as 'all' | 'location' | 'integration',
    scopeId: '',
  });

  const programs = useQuery<VendorRebateProgram[]>({ queryKey: VENDOR_REBATE_PROGRAMS_QUERY_KEY, queryFn: listVendorRebatePrograms });
  const stores = useQuery<VendorStoreOption[]>({ queryKey: VENDOR_REBATE_STORES_QUERY_KEY, queryFn: listVendorRebateStores, enabled: open });

  const store = stores.data?.find((s) => s.organizationId === form.store) || null;
  const scopeOptions = form.scopeKind === 'location' ? store?.locations || [] : form.scopeKind === 'integration' ? store?.integrations || [] : [];
  const scopeLabel = (p: VendorRebateProgram) =>
    p.scope.kind === 'location' ? t(`${NS}.scope.location`) : p.scope.kind === 'integration' ? t(`${NS}.scope.integration`) : t(`${NS}.scope.all`);

  const create = useMutation({
    mutationFn: () =>
      createScopedRebate({
        name: form.name.trim(),
        termsKind: form.termsKind,
        amount: Number(form.amount),
        startAt: form.startAt,
        endAt: form.endAt,
        ...(form.store !== ALL_STORES ? { storeOrganizationId: form.store } : {}),
        ...(form.scopeKind === 'location'
          ? { scope: { kind: 'location' as const, locationId: form.scopeId } }
          : form.scopeKind === 'integration'
            ? { scope: { kind: 'integration' as const, integrationId: form.scopeId } }
            : {}),
      }),
    onSuccess: (results) => {
      const created = results.filter((r) => r.outcome === 'created').length;
      setNotice(t(`${NS}.created`, { count: created, skipped: results.length - created }));
      setError(null);
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: VENDOR_REBATE_PROGRAMS_QUERY_KEY });
    },
    onError: (e: any) => setError(e?.message || t(`${NS}.error`)),
  });

  const valid =
    form.name.trim() && Number(form.amount) > 0 && form.startAt && form.endAt && (form.scopeKind === 'all' || (form.store !== ALL_STORES && form.scopeId));

  if (programs.isLoading) return <LoadingState />;
  if (programs.isError) return <Alert severity="error">{t(`${NS}.loadError`)}</Alert>;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} spacing={2}>
        <Typography variant="body2" color="text.secondary">{t(`${NS}.intro`)}</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setError(null); setNotice(null); setOpen(true); }}>
          {t(`${NS}.create`)}
        </Button>
      </Stack>
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
      {!programs.data || programs.data.length === 0 ? (
        <EmptyState title={t(`${NS}.emptyTitle`)} description={t(`${NS}.emptyDescription`)} size="compact" />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2 }}>
          <Stack divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
            {programs.data.map((p) => (
              <Stack key={p.id} direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ p: 2 }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2">{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {p.organizationName || t(`${NS}.allStores`)} · {scopeLabel(p)} · {t(`${NS}.kind.${p.kind}`)}
                    {p.startAt && p.endAt ? ` · ${new Date(p.startAt).toLocaleDateString()} – ${new Date(p.endAt).toLocaleDateString()}` : ''}
                  </Typography>
                </Box>
                <StatusChip label={t(`${NS}.status.${p.status}`)} tone={statusTone(p.status)} size="small" />
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <FormDialog
        open={open}
        title={t(`${NS}.dialogTitle`)}
        onClose={() => setOpen(false)}
        onSubmit={() => create.mutate()}
        submitLabel={t(`${NS}.submit`)}
        submitDisabled={!valid}
        loading={create.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">{t(`${NS}.dialogIntro`)}</Typography>
          <TextField label={t(`${NS}.name`)} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <TextField select label={t(`${NS}.termsKind`)} value={form.termsKind} onChange={(e) => setForm({ ...form, termsKind: e.target.value as 'instant_rebate' | 'trade_credit' })} fullWidth>
            <MenuItem value="instant_rebate">{t(`${NS}.kind.instant_rebate`)}</MenuItem>
            <MenuItem value="trade_credit">{t(`${NS}.kind.trade_credit`)}</MenuItem>
          </TextField>
          <NumberField label={t(`${NS}.amount`)} adornment="$" min={0} step="0.01" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} sx={{ width: '100%' }} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DateField label={t(`${NS}.startAt`)} value={form.startAt} onChange={(v) => setForm({ ...form, startAt: v })} sx={{ width: '100%' }} />
            <DateField label={t(`${NS}.endAt`)} value={form.endAt} min={form.startAt || undefined} onChange={(v) => setForm({ ...form, endAt: v })} sx={{ width: '100%' }} />
          </Stack>
          <TextField
            select
            label={t(`${NS}.store`)}
            value={form.store}
            onChange={(e) => setForm({ ...form, store: e.target.value, scopeKind: 'all', scopeId: '' })}
            helperText={stores.isLoading ? undefined : t(`${NS}.storeHelp`)}
            fullWidth
          >
            <MenuItem value={ALL_STORES}>{t(`${NS}.allVerifiedStores`)}</MenuItem>
            {(stores.data || []).map((s) => (
              <MenuItem key={s.organizationId} value={s.organizationId}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t(`${NS}.appliesTo`)}
            value={form.scopeKind}
            onChange={(e) => setForm({ ...form, scopeKind: e.target.value as 'all' | 'location' | 'integration', scopeId: '' })}
            disabled={form.store === ALL_STORES}
            helperText={t(`${NS}.appliesToHelp`)}
            fullWidth
          >
            <MenuItem value="all">{t(`${NS}.scope.all`)}</MenuItem>
            <MenuItem value="location">{t(`${NS}.scope.location`)}</MenuItem>
            <MenuItem value="integration">{t(`${NS}.scope.integration`)}</MenuItem>
          </TextField>
          {form.scopeKind !== 'all' && (
            <TextField select label={t(`${NS}.scope.${form.scopeKind}`)} value={form.scopeId} onChange={(e) => setForm({ ...form, scopeId: e.target.value })} fullWidth>
              {scopeOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>
              ))}
            </TextField>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </FormDialog>
    </Box>
  );
};
